import { Extension } from '@tiptap/core';

const DEFAULT_MARGIN_WIDTH = 4;

/**
 * Format a line number as a left-aligned string padded to marginWidth.
 * When the number exceeds the available digits (e.g. line 100 with
 * marginWidth 2), wrap via modulo and zero-pad.
 */
function formatMargin(lineNumber, marginWidth) {
  const maxNumber = Math.pow(10, marginWidth);
  if (lineNumber >= maxNumber) {
    const wrapped = lineNumber % maxNumber;
    return String(wrapped).padStart(marginWidth, '0');
  }
  return String(lineNumber).padEnd(marginWidth, ' ');
}

/**
 * Check if the document has any line that would receive a number when
 * numbering is applied (i.e. any speech-type line). Used to warn the user
 * when they try to number a document with nothing to number. The rule is
 * the same in both transcription modes.
 */
function documentHasNumberableLines(doc) {
  let found = false;

  doc.descendants((node) => {
    if (node.type.name === 'paragraph') {
      if (node.attrs.lineType === 'speech') {
        found = true;
      }
      return false;
    }
  });

  return found;
}

/**
 * Check if the document currently has line numbers applied.
 * Uses the marginContent attribute on paragraph nodes — a non-empty string
 * means a margin prefix was inserted (or inherited from a split).
 */
function documentHasLineNumbers(doc) {
  let found = false;

  doc.descendants((node) => {
    if (node.type.name === 'paragraph') {
      if (node.attrs.marginContent) {
        found = true;
      }
      return false; // don't descend into paragraph children
    }
  });

  return found;
}

const LineNumbering = Extension.create({
  name: 'lineNumbers',

  addStorage() {
    return { marginWidth: DEFAULT_MARGIN_WIDTH, mode: 'multimodal', multilingual: false };
  },

  addCommands() {
    return {
      addLineNumbers:
        () =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;

          const marginWidth = editor.storage.lineNumbers.marginWidth;
          const blankMargin = ' '.repeat(marginWidth);
          let speechCount = 0;

          // Collect all paragraphs with their positions first
          const paragraphs = [];
          tr.doc.descendants((node, pos) => {
            if (node.type.name === 'paragraph') {
              paragraphs.push({ node, pos });
              return false;
            }
          });

          for (const { node, pos } of paragraphs) {
            let prefix;
            if (node.attrs.lineType === 'speech') {
              speechCount++;
              prefix = formatMargin(speechCount, marginWidth);
            } else if (
              node.attrs.lineType === 'embodied' ||
              node.attrs.lineType === 'gloss' ||
              node.attrs.lineType === 'translation'
            ) {
              // Unnumbered annotation lines get a blank margin so their text
              // aligns under the numbered line's text.
              prefix = blankMargin;
            } else {
              continue; // default lines: no margin, stay flush left
            }

            // Map position through prior operations in this transaction
            const mappedPos = tr.mapping.map(pos);

            // Store the exact prefix being inserted as marginContent
            tr.setNodeMarkup(mappedPos, undefined, {
              ...node.attrs,
              marginContent: prefix,
            });

            // Insert the prefix at the start of the paragraph's text content
            const insertPos = tr.mapping.map(pos + 1);
            tr.insertText(prefix, insertPos, insertPos);
          }

          dispatch(tr);
          return true;
        },

      removeLineNumbers:
        () =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;

          const marginWidth = editor.storage.lineNumbers.marginWidth;
          // Fallback regex: digits followed by optional spaces, totaling marginWidth chars
          const fallbackPattern = new RegExp(`^\\d{1,${marginWidth}}\\s*$`);

          // Collect all paragraphs with their positions
          const paragraphs = [];
          tr.doc.descendants((node, pos) => {
            if (node.type.name === 'paragraph') {
              paragraphs.push({ node, pos });
              return false;
            }
          });

          // Track which paragraphs were handled in the primary pass
          const handledPositions = new Set();

          // === PRIMARY PASS ===
          // Process paragraphs that have a non-empty marginContent attribute
          for (const { node, pos } of paragraphs) {
            const stored = node.attrs.marginContent;
            if (!stored) continue;

            const mappedPos = tr.mapping.map(pos);

            if (node.textContent.startsWith(stored)) {
              // Content starts with the stored prefix — safe to delete
              tr.setNodeMarkup(mappedPos, undefined, {
                ...node.attrs,
                marginContent: '',
              });

              const from = tr.mapping.map(pos + 1);
              const to = tr.mapping.map(pos + 1 + stored.length);
              tr.delete(from, to);
            } else {
              // Content does NOT start with stored prefix —
              // margin was edited or attribute was inherited incorrectly.
              // Clear the attribute without deleting any text.
              tr.setNodeMarkup(mappedPos, undefined, {
                ...node.attrs,
                marginContent: '',
              });
            }

            handledPositions.add(pos);
          }

          // === FALLBACK PASS ===
          // Scan remaining paragraphs for lines that visually have a
          // numbered prefix but were not caught by the primary pass
          for (const { node, pos } of paragraphs) {
            if (handledPositions.has(pos)) continue;

            const text = node.textContent;
            if (text.length < marginWidth) continue;

            const slice = text.slice(0, marginWidth);
            if (fallbackPattern.test(slice)) {
              // Looks like a line number prefix — delete it
              const mappedPos = tr.mapping.map(pos);

              tr.setNodeMarkup(mappedPos, undefined, {
                ...node.attrs,
                marginContent: '',
              });

              const from = tr.mapping.map(pos + 1);
              const to = tr.mapping.map(pos + 1 + marginWidth);
              tr.delete(from, to);
            }
          }

          dispatch(tr);
          return true;
        },
    };
  },
});

export { DEFAULT_MARGIN_WIDTH, documentHasLineNumbers, documentHasNumberableLines };
export default LineNumbering;
